import { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  TextField,
  Chip,
  Skeleton,
  Alert,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Button,
  InputAdornment,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  TrendingUp as TrendingUpIcon,
  Business as BusinessIcon,
  Inventory as InventoryIcon,
  Refresh as RefreshIcon,
  AccountBalance as AccountBalanceIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { projectService, Project } from '../services/projectService';
import { useThemeMode } from '../contexts/ThemeContext';
import ProjectCard from '../components/ProjectCard';
import InvestmentModal from '../components/InvestmentModal';
import Loader from '../components/Loader';
import { useAuth } from '../contexts/AuthContext';

const DashboardInvestor = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';

  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [assetTypeFilter, setAssetTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [minValuation, setMinValuation] = useState<number | ''>('');
  const [maxValuation, setMaxValuation] = useState<number | ''>('');
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [investmentModalOpen, setInvestmentModalOpen] = useState(false);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await projectService.getProjects();

      let projectsData: Project[] = [];
      if (response.projects && Array.isArray(response.projects)) {
        projectsData = response.projects;
      } else if (Array.isArray(response)) {
        projectsData = response;
      }

      setProjects(projectsData);
    } catch (err) {
      console.error('Failed to fetch projects:', err);
      setError('Unable to load projects. Please check your connection and try again.');
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInvest = async (project: Project) => {
    setSelectedProject(project);
    setInvestmentModalOpen(true);
  };

  const handleInvestmentModalClose = () => {
    setInvestmentModalOpen(false);
    setSelectedProject(null);
    fetchProjects();
  };

  const clearFilters = () => {
    setSearchTerm('');
    setAssetTypeFilter('');
    setStatusFilter('');
    setMinValuation('');
    setMaxValuation('');
  };

  const filteredProjects = useMemo(() => {
    return projects.filter(project => {
      const matchesSearch =
        (project.title?.toLowerCase().includes(searchTerm.toLowerCase()) || false) ||
        (project.description?.toLowerCase().includes(searchTerm.toLowerCase()) || false) ||
        (project.location?.toLowerCase().includes(searchTerm.toLowerCase()) || false);

      const matchesAssetType = !assetTypeFilter || project.project_type === assetTypeFilter;
      const matchesStatus = !statusFilter || project.status === statusFilter;
      const matchesMinValuation = !minValuation || (project.valuation_usd && project.valuation_usd >= minValuation);
      const matchesMaxValuation = !maxValuation || (project.valuation_usd && project.valuation_usd <= maxValuation);

      return matchesSearch && matchesAssetType && matchesStatus && matchesMinValuation && matchesMaxValuation;
    });
  }, [projects, searchTerm, assetTypeFilter, statusFilter, minValuation, maxValuation]);

  // Calculate real stats from projects
  const stats = useMemo(() => {
    const tokenizedProjects = projects.filter(p => p.status === 'tokenized');
    const totalVolume = tokenizedProjects.reduce((sum, p) => {
      return sum + ((p.valuation_usd || 0) * ((p.tokenized_percent || 0) / 100));
    }, 0);

    const formatCurrency = (amount: number) => {
      if (amount >= 1000000) return `$${(amount / 1000000).toFixed(1)}M`;
      if (amount >= 1000) return `$${(amount / 1000).toFixed(0)}K`;
      return `$${amount.toFixed(0)}`;
    };

    return [
      {
        label: 'Total Volume',
        value: formatCurrency(totalVolume),
        icon: TrendingUpIcon,
        color: '#3B82F6',
        bgColor: isDark ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.08)',
        borderColor: isDark ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.15)',
      },
      {
        label: 'Tokenized Assets',
        value: tokenizedProjects.length.toString(),
        icon: BusinessIcon,
        color: '#10B981',
        bgColor: isDark ? 'rgba(16, 185, 129, 0.1)' : 'rgba(16, 185, 129, 0.08)',
        borderColor: isDark ? 'rgba(16, 185, 129, 0.2)' : 'rgba(16, 185, 129, 0.15)',
      },
      {
        label: 'Total Projects',
        value: projects.length.toString(),
        icon: InventoryIcon,
        color: '#F59E0B',
        bgColor: isDark ? 'rgba(245, 158, 11, 0.1)' : 'rgba(245, 158, 11, 0.08)',
        borderColor: isDark ? 'rgba(245, 158, 11, 0.2)' : 'rgba(245, 158, 11, 0.15)',
      },
    ];
  }, [projects, isDark]);

  const assetTypes = [
    { value: 'real_estate', label: 'Real Estate' },
    { value: 'art', label: 'Art' },
    { value: 'invoice', label: 'Invoice' },
    { value: 'equipment', label: 'Equipment' },
    { value: 'agriculture', label: 'Agriculture' },
  ];

  const statusOptions = [
    { value: 'submitted', label: 'Submitted' },
    { value: 'approved', label: 'Approved' },
    { value: 'tokenized', label: 'Tokenized' },
  ];

  const hasActiveFilters = searchTerm || assetTypeFilter || statusFilter || minValuation || maxValuation;

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 4 }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Typography variant="h3" sx={{ fontWeight: 700, color: 'text.primary' }}>
              {user?.role === 'admin' ? 'Admin Dashboard' : 'Investment Opportunities'}
            </Typography>
            {user?.role === 'admin' && (
              <Chip
                label="Admin Access"
                size="small"
                sx={{
                  background: 'linear-gradient(135deg, #F59E0B, #EF4444)',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '0.7rem',
                }}
              />
            )}
          </Box>
          <Typography variant="body1" color="text.secondary">
            {user?.role === 'admin'
              ? 'Manage and oversee all tokenized assets on the platform'
              : 'Discover and invest in vetted, tokenized real-world assets'}
          </Typography>
        </Box>

        {/* Admin Alert */}
        {user?.role === 'admin' && (
          <Alert
            severity="info"
            sx={{
              mb: 3,
              background: isDark ? 'rgba(14, 165, 233, 0.1)' : 'rgba(14, 165, 233, 0.08)',
              border: `1px solid ${isDark ? 'rgba(14, 165, 233, 0.3)' : 'rgba(14, 165, 233, 0.2)'}`,
              '& .MuiAlert-icon': { color: '#0EA5E9' },
            }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => navigate('/admin')}
                sx={{ fontWeight: 600 }}
              >
                Go to Admin Panel
              </Button>
            }
          >
            <strong>Admin Access:</strong> Review and approve pending projects in the Admin Panel.
          </Alert>
        )}

        {/* Error State */}
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 3 }}
            action={
              <Button color="inherit" size="small" onClick={fetchProjects}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {stats.map((stat, index) => (
            <Grid item xs={12} md={4} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <Card
                  sx={{
                    background: isDark ? 'rgba(17, 24, 39, 0.8)' : 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: 2,
                          background: stat.bgColor,
                          border: `1px solid ${stat.borderColor}`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <stat.icon sx={{ fontSize: '1.5rem', color: stat.color }} />
                      </Box>
                      <Box>
                        {loading ? (
                          <Skeleton variant="text" width={80} height={32} />
                        ) : (
                          <Typography variant="h4" sx={{ fontWeight: 700, color: stat.color }}>
                            {stat.value}
                          </Typography>
                        )}
                        <Typography variant="body2" color="text.secondary">
                          {stat.label}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        {/* Filters */}
        <Card
          sx={{
            mb: 4,
            background: isDark ? 'rgba(17, 24, 39, 0.6)' : 'rgba(255, 255, 255, 0.9)',
            border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
          }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', color: 'text.primary' }}>
                <FilterIcon sx={{ mr: 1 }} />
                Filters
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {hasActiveFilters && (
                  <Button
                    size="small"
                    startIcon={<ClearIcon />}
                    onClick={clearFilters}
                    sx={{ color: 'text.secondary' }}
                  >
                    Clear All
                  </Button>
                )}
                <Button
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={fetchProjects}
                  disabled={loading}
                  sx={{ color: 'text.secondary' }}
                >
                  Refresh
                </Button>
              </Box>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Search"
                  placeholder="Search by name, description..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: 'text.secondary', fontSize: '1.2rem' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Asset Type</InputLabel>
                  <Select
                    value={assetTypeFilter}
                    onChange={(e) => setAssetTypeFilter(e.target.value)}
                    label="Asset Type"
                  >
                    <MenuItem value="">All Types</MenuItem>
                    {assetTypes.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="">All Status</MenuItem>
                    {statusOptions.map((status) => (
                      <MenuItem key={status.value} value={status.value}>
                        {status.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} md={2.5}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Min Valuation ($)"
                  value={minValuation}
                  onChange={(e) => setMinValuation(e.target.value ? Number(e.target.value) : '')}
                />
              </Grid>
              <Grid item xs={6} md={2.5}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Max Valuation ($)"
                  value={maxValuation}
                  onChange={(e) => setMaxValuation(e.target.value ? Number(e.target.value) : '')}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Projects Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary' }}>
            Available Projects
          </Typography>
          <Chip
            label={loading ? 'Loading...' : `${filteredProjects.length} project${filteredProjects.length !== 1 ? 's' : ''}`}
            sx={{
              background: isDark ? 'rgba(59, 130, 246, 0.15)' : 'rgba(59, 130, 246, 0.1)',
              border: `1px solid ${isDark ? 'rgba(59, 130, 246, 0.3)' : 'rgba(59, 130, 246, 0.2)'}`,
              color: '#3B82F6',
              fontWeight: 500,
            }}
          />
        </Box>

        {/* Projects Grid */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <Loader size={60} variant="walk" text="Loading projects..." />
          </Box>
        ) : filteredProjects.length === 0 ? (
          <Card
            sx={{
              textAlign: 'center',
              py: 8,
              background: isDark ? 'rgba(17, 24, 39, 0.6)' : 'rgba(255, 255, 255, 0.9)',
              border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
            }}
          >
            <CardContent>
              <AccountBalanceIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {hasActiveFilters ? 'No matching projects found' : 'No projects available yet'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {hasActiveFilters
                  ? 'Try adjusting your filters or search terms'
                  : 'Be the first to submit an asset for tokenization'}
              </Typography>
              {hasActiveFilters ? (
                <Button variant="outlined" onClick={clearFilters}>
                  Clear Filters
                </Button>
              ) : (
                <Button variant="contained" onClick={() => navigate('/submit')}>
                  Submit Your Asset
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <Grid container spacing={3}>
            {filteredProjects.map((project, index) => (
              <Grid item xs={12} md={4} key={project.id}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  style={{ height: '100%' }}
                >
                  <ProjectCard project={project} onInvest={handleInvest} />
                </motion.div>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Investment Modal */}
        <InvestmentModal
          open={investmentModalOpen}
          onClose={handleInvestmentModalClose}
          project={selectedProject}
        />
      </Container>
    </Box>
  );
};

export default DashboardInvestor;
